import * as datefns from 'date-fns'
import { IssueEntity } from '../../types/IssueEntity'
import { hasSourceOfType } from './calculateIssueStatus'

type CategorizableIssue = Pick<
    IssueEntity,
    | 'statusComplete'
    | 'cancelled'
    | 'type'
    | 'subEndDate'
    | 'subStartDate'
    | 'ipoTradingDate'
    | 'publicationDate'
    | 'unitSubTotal'
>

const datePart = (date?) => (date ? datefns.startOfDay(new Date(date)) : null)

export const getIssueDateToFilter = (issue: CategorizableIssue) => {
    if (issue.type === 'IPO') {
        return issue.subEndDate
    }
    return issue.type === 'Rights issue' ? issue.subEndDate : issue.publicationDate
}

export const isPreviousIssue = (issue: CategorizableIssue) => {
    const now = datePart(new Date()) as Date

    const subStartDate = datePart(issue.subStartDate)
    const subEndDate = datePart(issue.subEndDate)

    if (hasSourceOfType(issue, 'Cancellation')) {
        return true
    }

    if (['Rights issue', 'IPO'].includes(issue.type)) {
        if (!subStartDate || !subEndDate) {
            return false
        } else if (datefns.isAfter(now, subEndDate)) {
            return true
        }
    } else if (['Directed issue', 'Non-cash issue'].includes(issue.type)) {
        if (issue.unitSubTotal !== null) {
            return true
        }
    } else if (issue.type === 'ABB Directed issue') {
        if (issue.unitSubTotal !== null) {
            return true
        }
    }

    return false
}

const isCurrentIssue = (issue: CategorizableIssue) => {
    const now = datePart(new Date()) as Date

    const subStartDate = datePart(issue.subStartDate)
    const subEndDate = datePart(issue.subEndDate)

    if (['Rights issue', 'IPO'].includes(issue.type)) {
        if (!subStartDate || !subEndDate) return false
        if (!datefns.isBefore(now, subStartDate) && !datefns.isAfter(now, subEndDate)) {
            return true
        }
    } else if (issue.type === 'ABB Directed issue') {
        if (issue.unitSubTotal === null) {
            return true
        }
    }

    return false
}

const isFutureIssue = (issue: CategorizableIssue) => {
    const now = datePart(new Date()) as Date

    const subStartDate = datePart(issue.subStartDate)
    const subEndDate = datePart(issue.subEndDate)

    if (['Rights issue', 'IPO'].includes(issue.type)) {
        if (!subStartDate || !subEndDate) {
            return true
        } else if (datefns.isBefore(now, subStartDate)) {
            return true
        }
    } else if (['Directed issue', 'Non-cash issue'].includes(issue.type)) {
        if (issue.unitSubTotal === null) {
            return true
        }
    }

    return false
}

/**
 * Categorize issues into previous, current and future, by looking at the current date
 * and comparing it to subStartDate and subEndDate in the issues.
 *
 * @param {Array} issues List of issues to categorize
 * @returns {Object} Object with keys previousIssues, currentIssues and futureIssues
 */

export const categorizeIssues = <T extends CategorizableIssue>(issues: T[]) => {
    const previousIssues: T[] = []
    const currentIssues: T[] = []
    const futureIssues: T[] = []

    issues.forEach((issue) => {
        if (isFutureIssue(issue)) {
            futureIssues.push(issue)
            return
        }

        if (isCurrentIssue(issue)) {
            currentIssues.push(issue)
            return
        }

        if (isPreviousIssue(issue)) {
            previousIssues.push(issue)
            return
        }
    })
    return { previousIssues, currentIssues, futureIssues }
}
